import React from "react"
import { type MonetaryValue, type MoneyFlow } from "@digits-graphql/frontend/graphql-bearer"
import { SvgCheckCircle } from "@digits-shared/components/SVGIcons/line/CheckCircle.svg"
import { SvgCopy02 } from "@digits-shared/components/SVGIcons/line/Copy02.svg"
import { DigitsButton } from "@digits-shared/DesignSystem/Button"
import stringHelper from "@digits-shared/helpers/stringHelper"
import styled from "styled-components"

export const CopyableAmountContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;

  &::after {
    content: "";
    position: absolute;
    top: 0;
    bottom: 0;
    right: -5px;
    width: 10px;
  }

  ${DigitsButton} {
    cursor: pointer;
  }
`

const StyledCopyableAmount = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: flex-end;

  ${CopyableAmountContainer} {
    opacity: 0;
    transition: opacity 200ms 250ms ease-in-out;
    pointer-events: none;
  }

  &:hover {
    ${CopyableAmountContainer} {
      opacity: 1;
      pointer-events: auto;
    }
  }

  &:has(button:focus-visible) {
    ${CopyableAmountContainer} {
      transition: none;
      opacity: 1;
      pointer-events: auto;
    }
  }

  &.default {
    ${CopyableAmountContainer} {
      position: relative;
      margin-right: 5px;
    }
  }

  &.absolute {
    ${CopyableAmountContainer} {
      position: absolute;
      left: 0;
    }
  }
`

const ButtonBackground = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  border-radius: 50%;
  background-color: white;
`

function isMoneyFlow(value: MoneyFlow | MonetaryValue): value is MoneyFlow {
  return (value as MoneyFlow)?.value !== undefined
}

function isMonetaryValue(value: MoneyFlow | MonetaryValue): value is MonetaryValue {
  return (value as MonetaryValue)?.amount !== undefined
}

export const CopyableAmount: React.FC<
  React.PropsWithChildren<{
    amount: MoneyFlow | MonetaryValue
    $variant?: "absolute" | "default"
    className?: string
  }>
> = ({ amount, $variant = "default", className, children }) => {
  const moneyFlow = isMoneyFlow(amount) ? amount : undefined
  const value = moneyFlow ? moneyFlow.value : isMonetaryValue(amount) ? amount : undefined

  const [hasCopied, setHasCopied] = React.useState(false)
  const copyToClipboard = React.useCallback(
    async (event: React.MouseEvent) => {
      if (!value) return

      event.stopPropagation()

      try {
        const isNegative = moneyFlow && !moneyFlow.isNormal && value.amount !== 0
        const currency = value.amount / value.currencyMultiplier
        await navigator.clipboard.writeText(`${isNegative ? "-" : ""}${currency.toString()}`)
        setHasCopied(true)
      } catch (e) {
        TrackJS?.track(e)
      }
    },
    [moneyFlow, value]
  )

  React.useEffect(() => {
    let timer: NodeJS.Timeout | undefined
    if (hasCopied) {
      timer = setTimeout(() => {
        setHasCopied(false)
      }, 3000)
    }

    return () => clearTimeout(timer)
  }, [hasCopied])

  if (!value || value.amount === 0) return children

  return (
    <StyledCopyableAmount className={stringHelper.classNames($variant, className)}>
      <CopyableAmountContainer>
        <ButtonBackground />
        <DigitsButton
          $variant={hasCopied ? "primary-outline" : "secondary-dark"}
          $circle
          size="small"
          onClick={copyToClipboard}
        >
          {hasCopied ? <SvgCheckCircle /> : <SvgCopy02 />}
        </DigitsButton>
      </CopyableAmountContainer>
      {children}
    </StyledCopyableAmount>
  )
}
